import {tolgeeInstance} from 'tolgee';

import {LABEL_ID_SEPARATOR, UNKNOWN_MODEL_ID} from '~constants/constants';
import {theme} from '~constants/theme';
import {ImageSize} from '~utils/imageSizeCache';
import {AnyLabelledItem, getAliasFromItems, getColorFromItems, getUUID} from '~utils/miscUtils';
import {
  AnnotationLabel,
  Coordinate,
  ImageDataAnnotation,
  ImageLabelPayloadByModel,
  LabelType,
  PolygonOrClassOrKeypointAnnotationLabel,
} from '~redux/types/images';
import {Condition, Model} from '~redux/types/models';
import {PartialWithRequired} from '~redux/types/utils';
import {AnnotationItem, AnnotationItemsByModel} from '~redux/reducers/annotatorReducer';

import {isKeypointAnnotationLabel, isPolygonAnnotationLabel} from '~components/images/utils/annotation.utils';
import {isClassificationLabel, isPolygonLabel} from '~components/images/utils/classification.utils';
import {getInspectionSubType, getInspectionType} from '~components/models/models.utils';

export const CURATION_PIPELINE_USER_PREFIX = 'curation_run_curation_pipeline';

export function isCurationMachinePrediction(annotation: ImageDataAnnotation): boolean {
  return annotation.user.startsWith(CURATION_PIPELINE_USER_PREFIX);
}

export function getAnnotationLabelType(label: AnnotationLabel): LabelType {
  if (isKeypointAnnotationLabel(label)) {
    return 'keypoint';
  }

  if (isPolygonAnnotationLabel(label)) {
    return 'polygon';
  }

  return 'class';
}

function coordinatesToString(coordinates: Coordinate[]): string {
  return coordinates.map((c) => `${c.x}${c.y}`).join('');
}

export function offsetCoordinates(coordinates: Coordinate[], offsetX: number = 0, offsetY: number = 0): Coordinate[] {
  return coordinates.map((c) => ({x: c.x + offsetX, y: c.y + offsetY}));
}

export function clampCoordinatesToImage(coordinates: Coordinate[], imageSize?: ImageSize): Coordinate[] {
  return coordinates.map((c) => ({
    x: Math.min(Math.max(0, Math.round(c.x)), imageSize?.width ?? Infinity),
    y: Math.min(Math.max(0, Math.round(c.y)), imageSize?.height ?? Infinity),
  }));
}

export function clampLabelPayloadToImage(
  payloadByModel: ImageLabelPayloadByModel,
  imageSize?: ImageSize,
): ImageLabelPayloadByModel {
  return Object.fromEntries(
    Object.entries(payloadByModel).map(([modelId, labels]) => [
      modelId,
      labels.map((label) =>
        label.coordinates ? {...label, coordinates: clampCoordinatesToImage(label.coordinates, imageSize)} : label,
      ),
    ]),
  );
}

/**
 * Returns a unique ID for a polygon or class annotation label. If the label already has an ID (polygonId), it will be returned.
 */
export function getAnnotationItemId(
  label: PolygonOrClassOrKeypointAnnotationLabel,
  isMachinePrediction: boolean,
): string {
  const isPolygon = isPolygonLabel(label);
  const machinePredictionPrefix = isMachinePrediction ? 'machine' : '';
  if (isPolygon && 'polygonId' in label && label.polygonId) {
    return label.polygonId;
  } else if (isPolygon) {
    return machinePredictionPrefix + label.type + label.id + coordinatesToString(label.coordinates);
  } else if (isClassificationLabel(label)) {
    return machinePredictionPrefix + 'class' + label.id;
  }
  return getUUID();
}

interface CreateAnnotationItemArgs {
  annotationLabel: AnnotationLabel;
  imageDataAnnotation: ImageDataAnnotation;
  isMachinePrediction: boolean;
  labelItems: AnyLabelledItem[];
  models: Model[];
}

export const isSimilarityLabel = (labelId: string) => labelId.startsWith('SIMILARITY');

interface GetAnnotationColorArgs {
  labelId: string;
  labelItems: AnyLabelledItem[];
  options: {
    isUnknownMachinePrediction: boolean;
    isCurationMachinePrediction: boolean;
    modelId?: string;
    isUnstablePrediction?: boolean;
  };
}
export const getAnnotationColor = ({labelId, labelItems, options}: GetAnnotationColorArgs) => {
  const {isUnknownMachinePrediction, isCurationMachinePrediction, modelId, isUnstablePrediction} = options;
  if (isSimilarityLabel(labelId)) {
    return theme.palette.maddox.similarityLabel;
  }

  if (isUnstablePrediction) {
    // Unstable enhanced-testing predictions are shown in the "synthetic" bluish color, not the class color, so they
    // read as an enhanced-testing artifact rather than a normal defect prediction.
    return theme.palette.secondary.light;
  }

  if (isCurationMachinePrediction) {
    const filtered = modelId ? labelItems.filter((item) => item.id.endsWith(LABEL_ID_SEPARATOR + modelId)) : [];
    const scopedLabels = filtered.length > 0 ? filtered : labelItems;

    const firstNonClassLabel = scopedLabels.find((item) => item.type !== 'class');
    if (firstNonClassLabel) {
      return getColorFromItems(firstNonClassLabel.id, scopedLabels);
    }

    const firstNonOkLabel = scopedLabels.find((item) => item.condition !== Condition.OK);
    if (firstNonOkLabel) {
      return getColorFromItems(firstNonOkLabel.id, scopedLabels);
    }
  }

  if (isUnknownMachinePrediction) return theme.palette.maddox.black40;

  return getColorFromItems(labelId, labelItems);
};

const getAnnotationCondition = (labelId: string, labelItems: AnyLabelledItem[]) => {
  if (!labelId) return undefined;
  const labelItem = labelItems.find((item) => item.id === labelId);
  if (!labelItem) return undefined;

  const {condition} = labelItem;
  return condition;
};

export function createAnnotationItem({
  annotationLabel,
  isMachinePrediction,
  imageDataAnnotation,
  labelItems,
  models,
}: CreateAnnotationItemArgs): AnnotationItem {
  const {id: labelId, coordinates, probability, hiddenForSmartSession, area, modelVersion, unstable} = annotationLabel;
  const {modelId, updatedAt, user, isDefaultPrediction} = imageDataAnnotation;

  const userEmail = isMachinePrediction ? tolgeeInstance.t('maddoxAIMachinePrediction') : user;
  const isUnknownMachinePrediction = isMachinePrediction && modelId === UNKNOWN_MODEL_ID;
  const source =
    annotationLabel.source ??
    (!isMachinePrediction
      ? 'human'
      : isCurationMachinePrediction(imageDataAnnotation)
        ? 'curation_image_selection'
        : undefined);
  const isCurationPrediction = source === 'curation_image_selection' || source === 'curation_image_cleanup';
  const color = getAnnotationColor({
    labelId,
    labelItems,
    options: {
      isUnknownMachinePrediction,
      isCurationMachinePrediction: isCurationPrediction,
      modelId,
      isUnstablePrediction: unstable,
    },
  });
  const condition = getAnnotationCondition(labelId, labelItems);

  let updatedModelId: string | undefined = modelId;
  if (isMachinePrediction && modelId === UNKNOWN_MODEL_ID) {
    updatedModelId = modelId === UNKNOWN_MODEL_ID ? undefined : modelId;
  }

  return {
    changeType: 'none',
    labelId,
    modelId: updatedModelId,
    modelVersion,
    userEmail,
    updatedAt,
    isMachinePrediction,
    isUnknownMachinePrediction,
    isCurationMachinePrediction: isCurationPrediction,
    isDefaultPrediction: isDefaultPrediction ?? false,
    color,
    id: getAnnotationItemId(annotationLabel, isMachinePrediction),
    type: getAnnotationLabelType(annotationLabel),
    alias: getAliasFromItems(labelId, labelItems),
    inspectionType: getInspectionType(annotationLabel.id, models),
    inspectionSubType: getInspectionSubType(annotationLabel.id, models),
    coordinates: coordinates ?? undefined,
    probability: probability ?? undefined,
    hiddenForSmartSession: hiddenForSmartSession,
    area: area ?? undefined,
    condition: condition,
    source,
    unstable: unstable ?? false,
  };
}

export function getAnnotationItemById(
  annotationItemId: AnnotationItem['id'],
  annotationItems: AnnotationItem[],
): AnnotationItem | undefined {
  return annotationItems.find((item) => item.id === annotationItemId);
}

export function getModelIdForAnnotationItem(
  annotationItemId: AnnotationItem['id'],
  annotationItemsByModel: AnnotationItemsByModel,
): string | undefined {
  let modelId: string | undefined;
  Object.entries(annotationItemsByModel).forEach(([currentModelId, items]) => {
    if (items.find((item) => item.id === annotationItemId)) {
      modelId = currentModelId;
    }
  });
  return modelId;
}

export function updateAnnotationItem(
  itemToUpdate: PartialWithRequired<AnnotationItem, 'id'>,
  annotationItems: AnnotationItem[],
): AnnotationItem[] {
  const existingItem = getAnnotationItemById(itemToUpdate.id, annotationItems);
  if (!existingItem || existingItem.isMachinePrediction) {
    return annotationItems;
  }

  // New items should stay "new" even if they are modified
  const changeType = existingItem.changeType === 'new' ? 'new' : 'modified';
  return annotationItems.map((item) => {
    if (item.id === itemToUpdate.id) {
      return {
        ...item,
        ...itemToUpdate,
        changeType,
        updatedAt: Date.now(),
      };
    }
    return item;
  });
}

export function deleteAnnotationItem(annotationItemId: string, annotationItems: AnnotationItem[]): AnnotationItem[] {
  const itemToDelete = getAnnotationItemById(annotationItemId, annotationItems);
  if (!itemToDelete || itemToDelete.isMachinePrediction) {
    return annotationItems;
  }

  // delete the annotation item if it was previously "new"
  if (itemToDelete.changeType === 'new') {
    annotationItems = annotationItems.filter((item) => item.id !== itemToDelete.id);
  }

  // otherwise set the changeType to "deleted" if it was not previously "new"
  return annotationItems.map((item) => {
    if (item.id === itemToDelete.id) {
      return {
        ...item,
        changeType: 'deleted',
      };
    }
    return item;
  });
}

export function deleteAllKeypoints(annotationItems: AnnotationItem[]): AnnotationItem[] {
  // First, filter out any keypoints that were previously "new" - these can be completely removed
  const filteredItems = annotationItems.filter(
    (item) => !(item.type === 'keypoint' && !item.isMachinePrediction && item.changeType === 'new'),
  );

  // Then, mark remaining human keypoints as "deleted"
  return filteredItems.map((item) => {
    if (item.type === 'keypoint' && !item.isMachinePrediction && item.changeType !== 'deleted') {
      return {
        ...item,
        changeType: 'deleted',
      };
    }
    return item;
  });
}

/**
 * Adds an annotation item to the list of existing annotation items.
 *
 * Within the same model, an image can either have one classification item or one or more polygon items, but not both. That means:
 * - when adding a new classification, we must delete all items of the same model and then add the new classification
 * - when adding a new polygon, we must delete all human classification items for the same model
 *
 * In either case, we must keep machine predictions as they are.
 */
export function addAnnotationItem(
  itemToAdd: AnnotationItem,
  annotationItems: AnnotationItem[] | undefined,
): AnnotationItem[] {
  const updatedExistingItems: AnnotationItem[] = [];

  if (!annotationItems) {
    return [itemToAdd];
  }

  for (const item of annotationItems) {
    if (item.isMachinePrediction) {
      updatedExistingItems.push(item);
      continue;
    }

    if (item.type === 'class' && item.changeType !== 'new') {
      // get rid of all existing classification items - there can only be one classification item per model.
      // By checking for changeType !== 'new', we make sure that new items are fully deleted
      // (by not adding them to the updatedItems)
      updatedExistingItems.push({
        ...item,
        changeType: 'deleted',
      });
    } else if (itemToAdd.type === 'polygon' && item.type === 'polygon') {
      // when adding a polygon, keep other polygons as they are
      updatedExistingItems.push(item);
    }
  }

  return [...updatedExistingItems, itemToAdd];
}
